import { Button, LightBox, PhotoFrame, PhotoStrip } from "@instacover/ui"
import { createId as cuid } from "@paralleldrive/cuid2"
import { createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { DeleteButton } from "@/components/buttons/DeleteButton"
import { FullScreenButton } from "@/components/buttons/FullScreenButton"
import { ExtraIcons } from "@/components/icons/InstructionImageType"
import { BaseLayout } from "@/components/layouts/BaseLayout"
import { useImagePickerWithRotation } from "@/helpers/hooks/useImagePicker"
import { LINKS } from "@/routing/links"
import { GuideMakePhoto } from "@/sections/guide/GuideMakePhoto"
import { addClaimDetail, type ClaimDetailLocal, softDeleteClaimDetail, useClaimDetailsByMarker } from "@/services/dexie"
import { useClaimDetailSrc } from "@/utils/hooks/useClaimDetailSrc"

export const Route = createFileRoute("/$sessionId/_session/claim/markers/$markerId/details")({
  component: ClaimMarkerDetailsPage,
})

function ClaimMarkerDetailsPage() {
  const { sessionId, markerId } = Route.useParams()
  const { enableUpload } = useLoaderData({ from: "/$sessionId/_session/claim" })

  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: details } = useClaimDetailsByMarker(markerId)
  const [selectedDetailId, setSelectedDetailId] = useState<string>()

  const { file, select } = useImagePickerWithRotation({ enableUpload })
  const capturedFileRef = useRef<File>(undefined)

  // New capture — store the detail and select it
  useEffect(() => {
    if (!file || file === capturedFileRef.current) return
    capturedFileRef.current = file

    async function save(captured: File) {
      const id = cuid()
      const data = new Uint8Array(await captured.arrayBuffer())
      await addClaimDetail({ id, sessionId, markerId, data, mime: captured.type })
      setSelectedDetailId(id)
    }

    save(file)
  }, [file, sessionId, markerId])

  const detailList = details ?? []
  const selectedDetail = detailList.find((detail) => detail.id === selectedDetailId) ?? detailList.at(0)

  async function handleDelete(id: string) {
    await softDeleteClaimDetail(id)
    const remaining = detailList.filter((detail) => detail.id !== id)
    setSelectedDetailId(remaining.at(0)?.id)
  }

  // No detail captured yet
  if (detailList.length === 0) {
    return (
      <GuideMakePhoto
        type={ExtraIcons.DamageDetail}
        heading={t("claim.captureDetail.heading")}
        text={t("claim.captureDetail.description")}
        onMakePhoto={select}
        enableUpload={enableUpload}
      />
    )
  }

  // At least one detail captured
  return (
    <BaseLayout dataTestId="claim-marker-details">
      <div className="h-vh-header mx-auto flex max-w-2xl flex-col gap-2 py-6">
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Detail thumbnails */}
          <PhotoStrip.Root
            className="px-2"
            data-testid="claim-detail-thumbnails"
            activeId={selectedDetail?.id}
            onSelect={setSelectedDetailId}
          >
            {detailList.map((detail) => (
              <PhotoStrip.Item key={detail.id} id={detail.id}>
                <ClaimDetailImage detail={detail} />
              </PhotoStrip.Item>
            ))}
          </PhotoStrip.Root>

          {/* main image */}
          {selectedDetail && <SelectedDetail detail={selectedDetail} onDelete={handleDelete} />}
        </div>

        <div className="mx-auto mt-6 flex w-full max-w-120 shrink-0 flex-col gap-2 px-8">
          <Button
            className="w-full"
            disabled={detailList.length === 0}
            onClick={() => navigate({ to: LINKS.claimOverview(sessionId) })}
            data-testid="claim-detail-continue-btn"
          >
            {t("claim.captureDetail.buttonSecondary")}
          </Button>

          <Button variant="secondary" className="w-full" onClick={select} data-testid="claim-detail-add-btn">
            {t("claim.captureDetail.buttonPrimary")}
          </Button>
        </div>
      </div>
    </BaseLayout>
  )
}

function ClaimDetailImage(props: { detail: ClaimDetailLocal }) {
  const { detail } = props
  const src = useClaimDetailSrc(detail)

  if (!src) return null

  return <img src={src} alt="detail" />
}

function SelectedDetail(props: { detail: ClaimDetailLocal; onDelete: (id: string) => void }) {
  const { detail, onDelete } = props

  const { t } = useTranslation()
  const [isFullScreen, setIsFullScreen] = useState(false)
  const src = useClaimDetailSrc(detail)

  function handleDelete() {
    setIsFullScreen(false)
    onDelete(detail.id)
  }

  return (
    <>
      <PhotoFrame
        className="mt-2"
        src={src}
        topRight={<DeleteButton onClick={handleDelete} />}
        bottomRight={<FullScreenButton onClick={() => setIsFullScreen(true)} />}
      />

      <LightBox
        open={isFullScreen}
        onOpenChange={setIsFullScreen}
        src={src}
        title={t("claim.captureDetail.heading")}
        topRight={<DeleteButton onClick={handleDelete} />}
        bottomRight={<FullScreenButton isFullScreen onClick={() => setIsFullScreen(false)} />}
      />
    </>
  )
}
